/**
 * reviews-wall.js — brand-neutral Reviews Wall section (see ADR-0003).
 *
 * Fetches customer reviews client-side from the SkinLab CSA Supabase project
 * (sentiment_items via PostgREST, publishable key) and renders them as
 * adaptive-height quote cards in a masonry wall or a scroll-snap slider.
 * All filtering is merchant-side (section settings) — visitors only see the
 * matching cards plus "Read more" / "Load more" (masonry) or prev/next
 * arrows (slider). The slider lazy-loads: visible cards × 2 up front, the
 * next batch fetched one viewport before the visitor reaches the end.
 * Desktop mice can also drag/flick the track (touch swipes scroll
 * natively); the release glide snaps to a card boundary.
 *
 * Query construction is a direct port of the CSA dashboard's fetchReviews()
 * (customer-sentiment-analysis/dashboard/src/lib/queries.js): same OR-arm
 * logic for mixed Google/Facebook mode, same hide-anonymous guard, same
 * keyset pagination. Card design is ported from GoogleReviewCard.jsx /
 * FacebookReviewCard.jsx with fixed canvas + font auto-shrink replaced by
 * fluid width + line clamp.
 */
(() => {
  if (customElements.get('reviews-wall')) return;

  const SELECT_COLUMNS = [
    'id', 'brand', 'source_platform', 'source_profile', 'author_name',
    'author_id', 'source_id', 'content', 'rating', 'recommendation_type',
    'source_created_at', 'has_media', 'media_urls', 'thumbnail_urls',
  ].join(',');

  // Photos attached to a review, best source first. `thumbnail_urls` are CSA's
  // rehosted copies in Supabase Storage (stable, content-addressed paths);
  // `media_urls` are Google's lh3 CDN originals, which can expire or be
  // throttled. Not every row is fully rehosted, so the originals are kept as
  // fallbacks after the rehosts rather than dropped.
  function reviewPhotos(row) {
    const ok = (u) => typeof u === 'string' && /^https:\/\//.test(u);
    const thumbs = (Array.isArray(row.thumbnail_urls) ? row.thumbnail_urls : []).filter(ok);
    const media = (Array.isArray(row.media_urls) ? row.media_urls : []).filter(ok);
    return { candidates: [...thumbs, ...media], count: Math.max(thumbs.length, media.length) };
  }

  // Official wordmarks, inlined exactly as in the CSA quote cards — no
  // external fetch, no font flicker. Google viewBox 272×92; Facebook paths
  // live in a pre-transformed <g>, viewBox 62.488×12.094.
  const GOOGLE_WORDMARK = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 272 92" role="img" aria-label="Google"><path fill="#EA4335" d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18C71.25 34.32 81.24 25 93.5 25s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44S80.99 39.2 80.99 47.18c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/><path fill="#FBBC05" d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/><path fill="#4285F4" d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z"/><path fill="#34A853" d="M225 3v65h-9.5V3h9.5z"/><path fill="#EA4335" d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z"/><path fill="#4285F4" d="M35.29 41.41V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91.36 15.93 16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49.01z"/></svg>';

  const FACEBOOK_WORDMARK = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 62.488 12.094" role="img" aria-label="Facebook"><g transform="matrix(0.076238, 0, 0, 0.076238, -9.869545, -16.954924)" fill="#1877F2"><path d="M 193.587 247.245 C 183.937 247.245 181.147 251.525 181.147 260.965 L 181.147 276.625 L 206.887 276.625 L 204.307 301.925 L 181.157 301.925 L 181.157 378.705 L 150.267 378.705 L 150.267 301.925 L 129.457 301.925 L 129.457 276.625 L 150.267 276.625 L 150.267 261.395 C 150.267 235.875 160.557 222.395 189.267 222.395 C 195.283 222.381 201.295 222.738 207.267 223.465 L 207.267 247.275 Z"/><path d="M 209.027 324.445 C 209.027 295.925 222.537 274.445 250.847 274.445 C 266.287 274.445 275.717 282.385 280.227 292.245 L 280.227 276.625 L 309.817 276.625 L 309.817 378.705 L 280.227 378.705 L 280.227 363.265 C 275.937 373.135 266.287 380.855 250.847 380.855 C 222.537 380.855 209.027 359.405 209.027 330.855 Z M 239.907 331.315 C 239.907 346.535 245.477 356.615 259.847 356.615 C 272.507 356.615 278.937 347.395 278.937 332.815 L 278.937 322.565 C 278.937 307.985 272.507 298.765 259.847 298.765 C 245.477 298.765 239.907 308.845 239.907 324.065 Z"/><path d="M 374.157 274.475 C 386.157 274.475 397.527 277.055 403.747 281.335 L 396.887 303.215 C 390.453 300.151 383.423 298.539 376.297 298.495 C 359.567 298.495 352.297 308.145 352.297 324.665 L 352.297 330.665 C 352.297 347.185 359.587 356.835 376.297 356.835 C 383.423 356.79 390.453 355.179 396.887 352.115 L 403.747 373.985 C 397.527 378.275 386.167 380.855 374.157 380.855 C 337.907 380.855 321.397 361.335 321.397 330.025 L 321.397 325.305 C 321.397 293.995 337.907 274.475 374.157 274.475 Z"/><path d="M 407.817 331.565 L 407.817 322.565 C 407.817 293.615 424.327 274.565 458.007 274.565 C 489.747 274.565 503.687 293.865 503.687 322.175 L 503.687 338.475 L 438.687 338.475 C 439.337 352.415 445.557 358.635 462.687 358.635 C 474.277 358.635 486.497 356.275 495.507 352.415 L 501.157 373.565 C 493.007 377.865 476.277 381.075 461.487 381.075 C 422.397 381.065 407.817 361.565 407.817 331.565 Z M 438.697 318.265 L 476.017 318.265 L 476.017 315.695 C 476.017 304.545 471.517 295.695 458.017 295.695 C 444.067 295.705 438.697 304.505 438.697 318.225 Z"/><path d="M 618.157 330.885 C 618.157 359.405 604.437 380.885 576.157 380.885 C 560.717 380.885 549.997 373.165 545.707 363.295 L 545.707 378.735 L 516.547 378.735 L 516.547 225.365 L 547.427 222.565 L 547.427 290.765 C 551.927 281.765 561.797 274.465 576.167 274.465 C 604.477 274.465 618.167 295.915 618.167 324.465 Z M 587.277 323.805 C 587.277 309.435 581.707 298.715 566.907 298.715 C 554.247 298.715 547.387 307.715 547.387 322.305 L 547.387 333.025 C 547.387 347.605 554.247 356.615 566.907 356.615 C 581.707 356.615 587.277 345.895 587.277 331.525 Z"/><path d="M 628.487 330.235 L 628.487 325.095 C 628.487 295.705 645.217 274.475 679.317 274.475 C 713.417 274.475 730.157 295.705 730.157 325.095 L 730.157 330.235 C 730.157 359.615 713.427 380.855 679.327 380.855 C 645.227 380.855 628.487 359.615 628.487 330.235 Z M 699.267 322.945 C 699.267 309.435 693.687 298.715 679.267 298.715 C 664.847 298.715 659.317 309.435 659.317 322.945 L 659.317 332.385 C 659.317 345.895 664.897 356.615 679.267 356.615 C 693.637 356.615 699.267 345.895 699.267 332.385 Z"/><path d="M 740.427 330.235 L 740.427 325.095 C 740.427 295.705 757.157 274.475 791.257 274.475 C 825.357 274.475 842.087 295.705 842.087 325.095 L 842.087 330.235 C 842.087 359.615 825.357 380.855 791.257 380.855 C 757.157 380.855 740.427 359.615 740.427 330.235 Z M 811.207 322.945 C 811.207 309.435 805.627 298.715 791.257 298.715 C 776.887 298.715 771.317 309.435 771.317 322.945 L 771.317 332.385 C 771.317 345.895 776.887 356.615 791.257 356.615 C 805.627 356.615 811.207 345.895 811.207 332.385 Z"/><path d="M 884.547 325.305 L 914.997 276.625 L 947.807 276.625 L 915.857 327.025 L 949.097 378.705 L 916.287 378.705 L 884.547 328.705 L 884.547 378.705 L 853.657 378.705 L 853.657 225.365 L 884.547 222.565 Z"/></g></svg>';

  const STAR_PATH = 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z';

  const GOOGLE_AVATAR_PALETTE = ['#4285F4', '#EA4335', '#FBBC04', '#34A853', '#9C27B0', '#FF6D00'];

  // FB pseudonym palette — Tailwind -700 shades, WCAG AA for white text
  // (ported from CSA anonymousReviewer.js; Meta strips reviewer identity on
  // every /ratings response, so FB rows are pseudonymised deterministically).
  const FB_AVATAR_PALETTE = [
    '#334155', '#0F766E', '#047857', '#0E7490', '#0369A1',
    '#1D4ED8', '#4338CA', '#6D28D9', '#A21CAF', '#BE123C',
  ];

  function fnv1a32(str) {
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h;
  }

  function reviewerIdentity(review) {
    const realName = (review.author_name || '').trim();
    if (review.source_platform === 'facebook_recommendations') {
      const hash = fnv1a32(review.source_id || review.id || '');
      const suffix = (hash & 0xffffff).toString(16).padStart(6, '0').toUpperCase();
      return {
        displayName: realName || `FB User · ${suffix}`,
        avatarColor: FB_AVATAR_PALETTE[hash % FB_AVATAR_PALETTE.length],
        avatarLetter: 'F',
      };
    }
    return { displayName: realName || 'A Google user', avatarColor: '#5f6368', avatarLetter: 'G' };
  }

  function formatReviewDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const dateParts = new Intl.DateTimeFormat('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Singapore',
    }).formatToParts(d);
    const get = (type) => dateParts.find((p) => p.type === type)?.value ?? '';
    const timeParts = new Intl.DateTimeFormat('en-GB', {
      hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Singapore',
    }).formatToParts(d);
    let hour = '', minute = '', dayPeriod = '';
    for (const p of timeParts) {
      if (p.type === 'hour') hour = p.value;
      else if (p.type === 'minute') minute = p.value;
      else if (p.type === 'dayPeriod') dayPeriod = p.value.toLowerCase().replace(/\./g, '');
    }
    return `${get('day')} ${get('month')} ${get('year')}, ${hour}:${minute}${dayPeriod}`;
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  class ReviewsWall extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-reviews-wall-config]');
      if (!configEl) return;

      let raw;
      try {
        raw = JSON.parse(configEl.textContent);
      } catch (err) {
        console.warn('[reviews-wall] invalid config JSON', err);
        return;
      }
      this.cfg = this.normalizeConfig(raw);
      this.isSlider = this.cfg.layout === 'slider';
      this.isMarquee = this.cfg.layout === 'marquee';

      this.grid = this.querySelector('[data-reviews-wall-grid]');
      this.status = this.querySelector('[data-reviews-wall-status]');
      this.moreBtn = this.querySelector('[data-reviews-wall-more]');
      this.lastRow = null;
      this.loading = false;
      this.exhausted = false;
      this.abort = new AbortController();
      // `error` does not bubble, so this listens in the CAPTURE phase.
      this.addEventListener('error', (e) => this.onPhotoError(e), { capture: true, signal: this.abort.signal });

      if (!this.cfg.supabaseUrl || !this.cfg.supabaseKey) {
        if (raw.designMode) this.showStatus('Reviews Wall: set the Supabase URL and publishable key in the section settings.');
        else console.warn('[reviews-wall] missing Supabase URL/key — section renders nothing');
        return;
      }

      // Marquee is its own pipeline: one capped fetch, no pagination, a
      // seamless GSAP loop per row. It shares only the query builder + card
      // renderer with masonry/slider.
      if (this.isMarquee) {
        this.setupMarquee();
        this.loadMarquee();
        return;
      }

      if (!this.grid) return;
      if (this.moreBtn) {
        this.moreBtn.addEventListener('click', () => this.loadPage(false));
      }
      if (this.isSlider) this.setupSlider();
      this.loadPage(true);
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      this.teardownMarquee();
    }

    normalizeConfig(raw) {
      const pick = (obj) => Object.keys(obj || {}).filter((k) => obj[k]);
      let sources = pick(raw.sources);
      // Mirror the CSA panel's "can't unselect everything" guard: both
      // sources unticked behaves as both ticked.
      if (sources.length === 0) sources = ['google_reviews', 'facebook_recommendations'];
      return {
        supabaseUrl: (raw.supabaseUrl || '').replace(/\/+$/, ''),
        supabaseKey: raw.supabaseKey || '',
        keyword: (raw.keyword || '').trim(),
        sources,
        brands: pick(raw.brands),
        outlets: (raw.outlets || '').split(',').map((s) => s.trim()).filter(Boolean),
        ratings: pick(raw.ratings),
        recTypes: pick(raw.recTypes),
        layout: raw.layout === 'slider' || raw.layout === 'marquee' ? raw.layout : 'masonry',
        columnsDesktop: Math.min(4, Math.max(2, parseInt(raw.columnsDesktop, 10) || 3)),
        pageSize: Math.max(1, parseInt(raw.pageSize, 10) || 6),
        showLoadMore: Boolean(raw.showLoadMore),
        showPhotos: raw.showPhotos !== false,
        marquee: this.normalizeMarquee(raw.marquee),
        labels: raw.labels || {},
      };
    }

    normalizeMarquee(m) {
      m = m || {};
      const num = (v, dflt) => (Number.isFinite(+v) ? +v : dflt);
      return {
        count: Math.min(200, Math.max(1, parseInt(m.count, 10) || 20)),
        rows: Math.min(3, Math.max(1, parseInt(m.rows, 10) || 2)),
        firstDirection: m.firstDirection === 'left' ? 'left' : 'right',
        cardWidth: Math.max(120, parseInt(m.cardWidth, 10) || 340),
        // baseSpeed is the divisor for the tween duration — never 0.
        baseSpeed: Math.max(1, num(m.baseSpeed, 40)),
        // hoverSpeed may be 0 (merchant's call — pauses on hover).
        hoverSpeed: Math.max(0, num(m.hoverSpeed, 1)),
        speedTransitionMs: Math.max(0, parseInt(m.speedTransitionMs, 10) || 600),
        photosOnly: m.photosOnly !== false,
      };
    }

    // Slider pages track the viewport: visible cards × 2 per fetch, so the
    // first paint costs exactly two "screens" of cards (4 columns → 8) and
    // the rest lazy-loads. Masonry keeps the merchant-set page size.
    pageSize() {
      return this.isSlider ? this.visibleCount() * 2 : this.cfg.pageSize;
    }

    // Must mirror the slider breakpoints in reviews-wall.css.
    visibleCount() {
      if (window.matchMedia('(max-width: 749px)').matches) return 1;
      if (window.matchMedia('(max-width: 989px)').matches) return 2;
      return this.cfg.columnsDesktop;
    }

    buildParams(cursor, limit, opts = {}) {
      const cfg = this.cfg;
      const params = new URLSearchParams();
      params.set('select', SELECT_COLUMNS);
      if (opts.photosOnly) params.set('has_media', 'is.true');
      params.set('order', 'source_created_at.desc,id.desc');
      params.set('limit', String(limit));
      params.set('source_platform', `in.(${cfg.sources.join(',')})`);
      if (cfg.brands.length) params.set('brand', `in.(${cfg.brands.join(',')})`);
      if (cfg.outlets.length) {
        // Outlet names contain spaces/parens — PostgREST in-lists need them quoted.
        const quoted = cfg.outlets.map((o) => `"${o.replace(/"/g, '')}"`).join(',');
        params.set('source_profile', `in.(${quoted})`);
      }

      const googleOnly = cfg.sources.length === 1 && cfg.sources[0] === 'google_reviews';
      const fbOnly = cfg.sources.length === 1 && cfg.sources[0] === 'facebook_recommendations';
      if (googleOnly) {
        // Exact-rating multi-select; empty set excludes all rows (CSA semantics).
        params.set('rating', `in.(${cfg.ratings.length ? cfg.ratings.join(',') : '-1'})`);
      } else if (fbOnly) {
        if (cfg.recTypes.length) params.set('recommendation_type', `in.(${cfg.recTypes.join(',')})`);
        else params.set('recommendation_type', 'eq.__never__');
      } else {
        // Mixed mode: rating arm gates Google rows, recommendation arm gates
        // FB rows; a row passes if the arm for its source is satisfied.
        const arms = [];
        if (cfg.ratings.length) {
          arms.push(`and(source_platform.eq.google_reviews,rating.in.(${cfg.ratings.join(',')}))`);
        }
        arms.push(
          cfg.recTypes.length
            ? `and(source_platform.eq.facebook_recommendations,recommendation_type.in.(${cfg.recTypes.join(',')}))`
            : 'and(source_platform.eq.facebook_recommendations,recommendation_type.eq.__never__)'
        );
        params.append('or', `(${arms.join(',')})`);
      }

      // Google rows without an author name are dropped (hideAnonymous=true in CSA).
      params.append('or', '(author_name.not.is.null,source_platform.neq.google_reviews)');

      if (cfg.keyword) params.set('content', `wfts(simple).${cfg.keyword}`);

      if (cursor) {
        params.append(
          'or',
          `(source_created_at.lt.${cursor.createdAt},and(source_created_at.eq.${cursor.createdAt},id.lt.${cursor.id}))`
        );
      }
      return params;
    }

    async loadPage(isFirst) {
      if (this.loading) return;
      this.loading = true;
      const cfg = this.cfg;
      const limit = this.pageSize();
      const cursor = isFirst || !this.lastRow
        ? null
        : { createdAt: this.lastRow.source_created_at, id: this.lastRow.id };
      if (this.moreBtn) this.moreBtn.disabled = true;
      try {
        const res = await fetch(`${cfg.supabaseUrl}/rest/v1/sentiment_items?${this.buildParams(cursor, limit)}`, {
          headers: { apikey: cfg.supabaseKey, Authorization: `Bearer ${cfg.supabaseKey}` },
          signal: this.abort.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const rows = await res.json();
        this.renderRows(rows);
        if (rows.length > 0) this.lastRow = rows[rows.length - 1];
        if (isFirst && rows.length === 0) {
          this.showStatus(cfg.labels.empty || 'No reviews to show yet.');
        } else {
          this.hideStatus();
        }
        this.exhausted = rows.length < limit;
        if (this.moreBtn) {
          this.moreBtn.hidden = this.exhausted || !cfg.showLoadMore;
          this.moreBtn.disabled = false;
        }
        this.updateArrows();
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.warn('[reviews-wall] fetch failed', err);
        // Fail soft: never break the page. Show the error text only when
        // nothing is rendered yet.
        if (!this.grid.children.length) this.showStatus(cfg.labels.error || 'Reviews are unavailable right now.');
        if (this.moreBtn) this.moreBtn.disabled = false;
      } finally {
        this.loading = false;
      }
    }

    setupSlider() {
      this.prevBtn = this.querySelector('[data-reviews-wall-prev]');
      this.nextBtn = this.querySelector('[data-reviews-wall-next]');
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByCard(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByCard(1));
      let ticking = false;
      this.grid.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            this.updateArrows();
            this.maybeLoadMore();
          });
        },
        { passive: true, signal: this.abort.signal }
      );
      window.addEventListener('resize', () => this.updateArrows(), {
        passive: true,
        signal: this.abort.signal,
      });
      this.setupDrag();
    }

    // Mouse drag-to-flick. Touch pointers are excluded — swiping already
    // scrolls the track natively. Mandatory snap re-snaps on every
    // programmatic scrollLeft write, so it is suspended (.is-dragging)
    // for the whole gesture and restored once the release glide settles.
    setupDrag() {
      const grid = this.grid;
      const opts = { signal: this.abort.signal };
      let dragging = false;
      let moved = false;
      let startX = 0;
      let startScroll = 0;
      let samples = [];

      grid.addEventListener(
        'pointerdown',
        (e) => {
          if (e.pointerType === 'touch' || e.button !== 0) return;
          dragging = true;
          moved = false;
          startX = e.clientX;
          startScroll = grid.scrollLeft;
          samples = [{ x: e.clientX, t: e.timeStamp }];
        },
        opts
      );

      grid.addEventListener(
        'pointermove',
        (e) => {
          if (!dragging) return;
          const dx = e.clientX - startX;
          if (!moved) {
            if (Math.abs(dx) < 6) return; // still a click, not a drag
            moved = true;
            try {
              grid.setPointerCapture(e.pointerId);
            } catch (err) {
              /* no active pointer (synthetic event) */
            }
            grid.classList.add('is-dragging');
            const sel = document.getSelection();
            if (sel) sel.removeAllRanges();
          }
          grid.scrollLeft = startScroll - dx;
          samples.push({ x: e.clientX, t: e.timeStamp });
          if (samples.length > 6) samples.shift();
        },
        opts
      );

      const release = (e) => {
        if (!dragging) return;
        dragging = false;
        if (!moved) return;
        // The click that follows a drag must not hit "Read more" etc.
        this.suppressClick = true;
        setTimeout(() => {
          this.suppressClick = false;
        }, 0);
        this.glide(e, samples);
      };
      grid.addEventListener('pointerup', release, opts);
      grid.addEventListener('pointercancel', release, opts);

      grid.addEventListener(
        'click',
        (e) => {
          if (this.suppressClick) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        { capture: true, signal: this.abort.signal }
      );

      // Avatars are <img> — native image drag would swallow the gesture.
      grid.addEventListener('dragstart', (e) => e.preventDefault(), opts);
    }

    // Project the flick (recent pointer velocity × glide time), round the
    // destination to a card boundary, smooth-scroll there.
    glide(e, samples) {
      const grid = this.grid;
      const last = samples[samples.length - 1];
      const ref = samples.find((s) => last.t - s.t <= 120) || samples[0];
      const dt = last.t - ref.t;
      const velocity = dt > 0 ? (last.x - ref.x) / dt : 0; // px/ms; +ve = dragged right
      const card = grid.querySelector('.reviews-wall__card');
      const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
      const step = card ? card.getBoundingClientRect().width + gap : grid.clientWidth;
      const projected = grid.scrollLeft - velocity * 280;
      const max = grid.scrollWidth - grid.clientWidth;
      const target = Math.max(0, Math.min(max, Math.round(projected / step) * step));
      grid.scrollTo({ left: target, behavior: 'smooth' });
      this.restoreSnapWhenSettled();
    }

    // Flipping mandatory snap back on mid-glide would yank the track, so
    // wait until the scroll position stops changing.
    restoreSnapWhenSettled() {
      if (this.settleTimer) clearInterval(this.settleTimer);
      let lastLeft = -1;
      this.settleTimer = setInterval(() => {
        if (!this.grid.isConnected) {
          clearInterval(this.settleTimer);
          return;
        }
        if (this.grid.scrollLeft === lastLeft) {
          clearInterval(this.settleTimer);
          this.settleTimer = null;
          this.grid.classList.remove('is-dragging');
          return;
        }
        lastLeft = this.grid.scrollLeft;
      }, 100);
    }

    scrollByCard(dir) {
      const card = this.grid.querySelector('.reviews-wall__card');
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.grid).columnGap) || 0;
      this.grid.scrollBy({ left: dir * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }

    // Lazy load: fetch the next batch one viewport before the end of the
    // loaded track, so cards are already there when the visitor arrives.
    maybeLoadMore() {
      if (!this.isSlider || this.exhausted || this.loading) return;
      if (this.grid.scrollLeft + this.grid.clientWidth * 2 >= this.grid.scrollWidth) {
        this.loadPage(false);
      }
    }

    updateArrows() {
      if (!this.prevBtn || !this.nextBtn) return;
      const maxScroll = this.grid.scrollWidth - this.grid.clientWidth;
      this.prevBtn.disabled = this.grid.scrollLeft <= 1;
      this.nextBtn.disabled = this.exhausted && this.grid.scrollLeft >= maxScroll - 1;
    }

    showStatus(text) {
      if (!this.status) return;
      this.status.textContent = text;
      this.status.hidden = false;
    }

    hideStatus() {
      if (this.status) this.status.hidden = true;
    }

    renderRows(rows) {
      const frag = document.createDocumentFragment();
      const cards = [];
      for (const row of rows) {
        const card = this.buildCard(row);
        cards.push(card);
        frag.appendChild(card);
      }
      this.grid.appendChild(frag);
      // Clamp detection needs layout — defer one frame, then reveal the
      // "Read more" toggle only where content actually overflows.
      requestAnimationFrame(() => {
        for (const card of cards) {
          const text = card.querySelector('.reviews-wall__text');
          const toggle = card.querySelector('.reviews-wall__readmore');
          if (text && toggle && text.scrollHeight > text.clientHeight + 1) toggle.hidden = false;
        }
        this.updateArrows();
      });
    }

    buildCard(row, opts = {}) {
      const isFacebook = row.source_platform === 'facebook_recommendations';
      const card = el('article', `reviews-wall__card reviews-wall__card--${isFacebook ? 'facebook' : 'google'}`);
      // Marquee cards are cloned to tile the loop and never expand, so the
      // "Read more" toggle is dropped (below) — clone events wouldn't survive
      // and a height change mid-loop would break the seamless measurement.
      if (opts.marquee) card.classList.add('reviews-wall__mq-card');

      const brandbar = el('div', 'reviews-wall__brandbar');
      const mark = el('span', 'reviews-wall__wordmark');
      mark.innerHTML = isFacebook ? FACEBOOK_WORDMARK : GOOGLE_WORDMARK;
      brandbar.appendChild(mark);
      brandbar.appendChild(el('span', 'reviews-wall__brandbar-label', isFacebook ? 'Recommendations' : 'Reviews'));
      card.appendChild(brandbar);

      const identity = reviewerIdentity(row);
      if (this.cfg.showPhotos && !isFacebook) {
        const photo = this.buildPhoto(row, identity);
        if (photo) {
          card.appendChild(photo);
          card.classList.add('reviews-wall__card--has-photo');
        }
      }
      const reviewer = el('div', 'reviews-wall__reviewer');
      reviewer.appendChild(this.buildAvatar(row, identity, isFacebook));
      const meta = el('div', 'reviews-wall__reviewer-meta');
      meta.appendChild(el('div', 'reviews-wall__name', identity.displayName));
      const subtitle = [row.source_profile, formatReviewDate(row.source_created_at)].filter(Boolean).join(' · ');
      meta.appendChild(el('div', 'reviews-wall__subtitle', subtitle));
      reviewer.appendChild(meta);
      card.appendChild(reviewer);

      if (!isFacebook && row.rating != null) {
        card.appendChild(this.buildStars(row.rating));
      } else if (isFacebook && row.recommendation_type) {
        const positive = row.recommendation_type === 'positive';
        const pill = el(
          'span',
          `reviews-wall__pill reviews-wall__pill--${positive ? 'positive' : 'negative'}`,
          positive ? '✓ Recommended' : '✗ Not recommended'
        );
        card.appendChild(pill);
      }

      if (row.content) {
        const text = el('p', 'reviews-wall__text', row.content);
        card.appendChild(text);
        if (!opts.marquee) {
          const toggle = el('button', 'reviews-wall__readmore', this.cfg.labels.readMore || 'Read more');
          toggle.type = 'button';
          toggle.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
          toggle.addEventListener('click', () => {
            const expanded = text.classList.toggle('is-expanded');
            toggle.textContent = expanded
              ? this.cfg.labels.readLess || 'Read less'
              : this.cfg.labels.readMore || 'Read more';
            toggle.setAttribute('aria-expanded', String(expanded));
          });
          card.appendChild(toggle);
        }
      }

      return card;
    }

    // The first attached photo, in a fixed-ratio box so a marquee card's size is
    // known before the image arrives (the loop measures its period from layout
    // offsets on build). The remaining candidates ride along in data-fallbacks
    // and are tried by the capture-phase error listener in connectedCallback,
    // which also covers marquee CLONES: cloneNode copies attributes but not
    // listeners, so a per-image handler would only ever rescue the base set.
    buildPhoto(row, identity) {
      const { candidates, count } = reviewPhotos(row);
      if (!candidates.length) return null;
      const fig = el('figure', 'reviews-wall__photo');
      const img = document.createElement('img');
      img.className = 'reviews-wall__photo-img';
      img.src = candidates[0];
      img.dataset.fallbacks = JSON.stringify(candidates.slice(1));
      img.alt = `Photo from ${identity.displayName}'s review`;
      img.width = 400;
      img.height = 300;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.referrerPolicy = 'no-referrer';
      fig.appendChild(img);
      if (count > 1) {
        const badge = el('span', 'reviews-wall__photo-count', `+${count - 1}`);
        badge.setAttribute('aria-label', `${count} photos in this review`);
        fig.appendChild(badge);
      }
      return fig;
    }

    onPhotoError(event) {
      const img = event.target;
      if (!(img instanceof HTMLImageElement) || !img.classList.contains('reviews-wall__photo-img')) return;
      let rest = [];
      try { rest = JSON.parse(img.dataset.fallbacks || '[]'); } catch (_) { rest = []; }
      if (rest.length) {
        img.dataset.fallbacks = JSON.stringify(rest.slice(1));
        img.src = rest[0];
        return;
      }
      const card = img.closest('.reviews-wall__card');
      const fig = img.closest('.reviews-wall__photo');
      if (fig) fig.remove();
      if (card) card.classList.remove('reviews-wall__card--has-photo');
    }

    buildAvatar(row, identity, isFacebook) {
      // Google stores the reviewer profile-photo URL in author_id.
      const photoUrl = !isFacebook ? row.author_id : null;
      if (photoUrl && /^https:\/\//.test(photoUrl)) {
        const img = document.createElement('img');
        img.className = 'reviews-wall__avatar';
        img.src = photoUrl;
        img.alt = '';
        img.width = 44;
        img.height = 44;
        img.loading = 'lazy';
        img.referrerPolicy = 'no-referrer';
        img.addEventListener('error', () => {
          img.replaceWith(this.buildInitialAvatar(identity.displayName, null));
        });
        return img;
      }
      if (isFacebook) return this.buildInitialAvatar(identity.avatarLetter, identity.avatarColor);
      return this.buildInitialAvatar(identity.displayName, null);
    }

    buildInitialAvatar(nameOrLetter, fixedColor) {
      const initial = (nameOrLetter || 'G').trim()[0].toUpperCase();
      let color = fixedColor;
      if (!color) {
        const idx = (nameOrLetter || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % GOOGLE_AVATAR_PALETTE.length;
        color = GOOGLE_AVATAR_PALETTE[idx];
      }
      const div = el('div', 'reviews-wall__avatar reviews-wall__avatar--initial', initial);
      div.style.background = color;
      return div;
    }

    buildStars(rating) {
      const wrap = el('div', 'reviews-wall__stars');
      wrap.setAttribute('role', 'img');
      wrap.setAttribute('aria-label', `${rating} out of 5 stars`);
      let html = '';
      for (let i = 1; i <= 5; i++) {
        html += `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${STAR_PATH}" fill="${i <= rating ? '#FBBC04' : '#DADCE0'}"/></svg>`;
      }
      wrap.innerHTML = html;
      return wrap;
    }

    /* ---------------------------------------------------------------------
     * Marquee layout — GSAP-powered dual-row auto-scroll.
     *
     * A fixed, capped set of reviews is fetched once and split alternately
     * across N rows; each row is an infinite, seamless loop (row 0 in the
     * configured direction, each subsequent row reversed). Hover eases the
     * hovered row's speed down (never a hard stop). Card opacity is driven
     * two mutually-exclusive ways: on pointer devices CSS `:hover` lifts the
     * hovered card; on small / touch screens a throttled ticker lifts cards
     * whose centre is inside the viewport (CSS owns the fade duration).
     *
     * Degrades to a static, swipeable strip with no GSAP or under
     * prefers-reduced-motion.
     * ------------------------------------------------------------------- */

    setupMarquee() {
      this.container = this.querySelector('[data-reviews-wall-marquee]');
      this.mqTracks = [...this.querySelectorAll('[data-reviews-wall-mq-track]')];
      this.mqTweens = [];
      this.mqCards = [];
      this.mqTickerCb = null;
      // Per-build listeners (row hover) live on their own controller so a
      // resize-rebuild can drop them cleanly without touching the persistent
      // resize / matchMedia listeners below (tied to the main abort signal).
      this.mqCtrl = new AbortController();

      // Small / touch screens animate opacity by viewport position; wide
      // pointer devices use CSS :hover. Including `(hover: none)` closes the
      // wide-touchscreen gap (>749px but no hover would otherwise get neither).
      this.mqViewportMM = window.matchMedia('(max-width: 749px), (hover: none)');
      this.mqViewportMM.addEventListener('change', () => this.applyViewportMode(), {
        signal: this.abort.signal,
      });

      let resizeTimer;
      window.addEventListener(
        'resize',
        () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => this.buildMarquee(), 200);
        },
        { passive: true, signal: this.abort.signal }
      );
    }

    async loadMarquee() {
      const cfg = this.cfg;
      const limit = cfg.marquee.count;
      try {
        const res = await fetch(`${cfg.supabaseUrl}/rest/v1/sentiment_items?${this.buildParams(null, limit, { photosOnly: cfg.marquee.photosOnly })}`, {
          headers: { apikey: cfg.supabaseKey, Authorization: `Bearer ${cfg.supabaseKey}` },
          signal: this.abort.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        this.mqReviews = await res.json();
        this.buildMarquee();
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.warn('[reviews-wall] marquee fetch failed', err);
        this.showStatus(cfg.labels.error || 'Reviews are unavailable right now.');
      }
    }

    buildMarquee() {
      const cfg = this.cfg;
      const m = cfg.marquee;
      const reviews = this.mqReviews || [];
      const tracks = this.mqTracks || [];
      if (!this.container || !tracks.length) return;

      // Always start from a clean slate — resize rebuilds re-enter here.
      this.teardownMarquee();

      if (!reviews.length) {
        this.showStatus(cfg.labels.empty || 'No reviews to show yet.');
        return;
      }
      this.hideStatus();

      // Distribute reviews alternately across the rows.
      const buckets = tracks.map(() => []);
      reviews.forEach((r, i) => buckets[i % tracks.length].push(r));

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const hasGsap = !!(window.gsap && window.gsap.to && window.gsap.utils);
      const staticMode = reduced || !hasGsap;
      this.container.classList.toggle('reviews-wall__marquee--static', staticMode);
      this.container.classList.toggle('reviews-wall__marquee--on', !staticMode);

      this.mqTweens = [];
      this.mqCards = [];

      tracks.forEach((track, ri) => {
        const bucket = buckets[ri].length ? buckets[ri] : reviews;
        const base = bucket.map((r) => this.buildCard(r, { marquee: true }));
        base.forEach((card) => track.appendChild(card));

        if (staticMode) {
          this.mqCards.push(...base);
          return;
        }

        const rowEl = track.parentElement;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        // The repeat period is the VISUAL tiling distance — from the first
        // base card's box to where the next repeat begins. Measured from
        // layout box offsets (NOT scrollWidth) so any ink that overflows a
        // card can't inflate the period and open a seam at each wrap.
        const firstCard = base[0];
        const lastCard = base[base.length - 1];
        const period = lastCard.offsetLeft - firstCard.offsetLeft + lastCard.offsetWidth + gap;
        if (period <= 0) {
          this.mqCards.push(...base);
          return;
        }

        // Tile enough copies to always cover the viewport plus one wrapped
        // period, so the seam is never visible.
        const viewport = rowEl.clientWidth || period;
        const copies = Math.max(2, Math.ceil((viewport + period) / period) + 1);
        for (let c = 1; c < copies; c++) {
          // Clones are purely visual tiling — hide them from assistive tech so
          // each review is announced once (from the base set), not per copy.
          base.forEach((card) => {
            const clone = card.cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            track.appendChild(clone);
          });
        }
        this.mqCards.push(...track.children);

        // Row 0 uses the configured direction; each subsequent row reverses.
        const movingRight = m.firstDirection === 'right' ? ri % 2 === 0 : ri % 2 === 1;
        const gsap = window.gsap;
        // Both directions tile copies rightward; only the start/end of x
        // differ, so the modulo wrap seam is identical and jump-free on load.
        gsap.set(track, { x: movingRight ? -period : 0 });
        const tween = gsap.to(track, {
          x: movingRight ? 0 : -period,
          duration: period / m.baseSpeed,
          ease: 'none',
          repeat: -1,
          modifiers: { x: gsap.utils.unitize(gsap.utils.wrap(-period, 0), 'px') },
        });
        this.mqTweens.push(tween);
        this.attachHoverSlowdown(rowEl, tween);
      });

      if (!staticMode) this.applyViewportMode();
    }

    // Ease the hovered row's speed toward hoverSpeed (via timeScale) and back
    // to full on leave — never a hard cut. overwrite:true stops the ease
    // tweens from stacking on rapid enter/leave.
    attachHoverSlowdown(rowEl, tween) {
      const gsap = window.gsap;
      const m = this.cfg.marquee;
      const dur = m.speedTransitionMs / 1000;
      const slowScale = m.baseSpeed > 0 ? m.hoverSpeed / m.baseSpeed : 1;
      const opts = { signal: this.mqCtrl.signal };
      rowEl.addEventListener(
        'mouseenter',
        () => gsap.to(tween, { timeScale: slowScale, duration: dur, ease: 'power2.out', overwrite: true }),
        opts
      );
      rowEl.addEventListener(
        'mouseleave',
        () => gsap.to(tween, { timeScale: 1, duration: dur, ease: 'power2.inOut', overwrite: true }),
        opts
      );
    }

    // Small / touch screens: lift a card's opacity while its centre is inside
    // the viewport, drop it as it starts to exit. Reads are batched then
    // writes applied (one layout flush) and throttled to ~10fps — plenty for
    // a slow marquee, and the 0.7↔1 fade timing is owned by CSS.
    applyViewportMode() {
      if (!this.container || !window.gsap || !window.gsap.ticker) return;
      if (this.mqTickerCb) {
        window.gsap.ticker.remove(this.mqTickerCb);
        this.mqTickerCb = null;
      }
      const on = this.mqViewportMM.matches && this.container.classList.contains('reviews-wall__marquee--on');
      this.container.classList.toggle('reviews-wall__marquee--viewport', on);
      if (!on) {
        for (const card of this.mqCards) card.classList.remove('is-active');
        return;
      }

      let lastT = 0;
      const cb = (time) => {
        if (time - lastT < 0.1) return;
        lastT = time;
        const vw = window.innerWidth;
        const cards = this.mqCards;
        const centers = new Array(cards.length);
        for (let i = 0; i < cards.length; i++) {
          const r = cards[i].getBoundingClientRect();
          centers[i] = r.left + r.width / 2;
        }
        for (let i = 0; i < cards.length; i++) {
          cards[i].classList.toggle('is-active', centers[i] >= 0 && centers[i] <= vw);
        }
      };
      this.mqTickerCb = cb;
      window.gsap.ticker.add(cb);
    }

    teardownMarquee() {
      const gsap = window.gsap;
      if (this.mqTweens) {
        for (const tween of this.mqTweens) {
          if (gsap) gsap.killTweensOf(tween);
          tween.kill();
        }
      }
      this.mqTweens = [];
      if (this.mqTickerCb && gsap && gsap.ticker) {
        gsap.ticker.remove(this.mqTickerCb);
      }
      this.mqTickerCb = null;
      if (this.mqCtrl) {
        this.mqCtrl.abort();
        this.mqCtrl = new AbortController();
      }
      for (const track of this.mqTracks || []) track.replaceChildren();
      this.mqCards = [];
    }
  }

  customElements.define('reviews-wall', ReviewsWall);
})();
